import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Upload,
  FileSpreadsheet,
  Check,
  AlertTriangle,
  ArrowRight,
  Building2,
  Package
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";

const ImportPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({
    articleNumberColumn: 0,
    priceNetColumn: null,
    priceGrossColumn: null,
    packagePriceNetColumn: null,
    packagePriceGrossColumn: null,
    packageSizeColumn: null,
    skipFirstRow: true
  });
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API}/suppliers`);
      setSuppliers(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Lieferanten");
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setResult(null);
    
    // Preview file
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
      const res = await axios.post(`${API}/import/preview`, formData);
      setPreview(res.data);
    } catch (err) {
      toast.error("Fehler beim Lesen der Datei");
    }
  };

  const handleImport = async () => {
    if (!file || !selectedSupplier) {
      toast.error("Bitte Lieferant und Datei auswählen");
      return;
    }
    
    setImporting(true);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('supplier_id', selectedSupplier);
    formData.append('article_number_column', mapping.articleNumberColumn);
    
    if (mapping.priceNetColumn !== null) {
      formData.append('price_net_column', mapping.priceNetColumn);
    }
    if (mapping.priceGrossColumn !== null) {
      formData.append('price_gross_column', mapping.priceGrossColumn);
    }
    if (mapping.packagePriceNetColumn !== null) {
      formData.append('package_price_net_column', mapping.packagePriceNetColumn);
    }
    if (mapping.packagePriceGrossColumn !== null) {
      formData.append('package_price_gross_column', mapping.packagePriceGrossColumn);
    }
    if (mapping.packageSizeColumn !== null) {
      formData.append('package_size_column', mapping.packageSizeColumn);
    }
    formData.append('skip_first_row', mapping.skipFirstRow);
    
    try {
      const res = await axios.post(`${API}/import/prices`, formData);
      setResult(res.data);
      toast.success(`${res.data.updated} Preise aktualisiert`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import fehlgeschlagen");
    } finally {
      setImporting(false);
    }
  };

  const ColumnSelect = ({ value, onChange, label }) => (
    <div className="space-y-1">
      <Label className="text-sm">{label}</Label>
      <Select value={value?.toString() || "none"} onValueChange={(v) => onChange(v === "none" ? null : parseInt(v))}>
        <SelectTrigger className="h-9">
          <SelectValue placeholder="Spalte wählen" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">-- Nicht zuordnen --</SelectItem>
          {preview?.headers?.map((header, idx) => (
            <SelectItem key={idx} value={idx.toString()}>
              Spalte {idx + 1}: {header || `(leer)`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="import-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
          Preisimport
        </h1>
        <p className="text-slate-500 mt-1">
          CSV-Dateien mit Lieferantenpreisen importieren
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload & Config */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Datei hochladen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Supplier Selection */}
            <div className="space-y-2">
              <Label>Lieferant *</Label>
              <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                <SelectTrigger>
                  <SelectValue placeholder="Lieferant wählen" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        {s.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>CSV-Datei *</Label>
              <Input
                type="file"
                accept=".csv,.txt"
                onChange={handleFileChange}
                className="cursor-pointer"
              />
              <p className="text-xs text-slate-500">
                CSV mit Semikolon (;) als Trennzeichen
              </p>
            </div>

            {/* Column Mapping */}
            {preview && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4 pt-4 border-t"
              >
                <h3 className="font-medium">Spalten-Zuordnung</h3>
                
                <ColumnSelect
                  value={mapping.articleNumberColumn}
                  onChange={(v) => setMapping({ ...mapping, articleNumberColumn: v })}
                  label="Artikelnummer *"
                />
                
                <div className="grid grid-cols-2 gap-3">
                  <ColumnSelect
                    value={mapping.packagePriceNetColumn}
                    onChange={(v) => setMapping({ ...mapping, packagePriceNetColumn: v })}
                    label="VPE-Preis Netto"
                  />
                  <ColumnSelect
                    value={mapping.packagePriceGrossColumn}
                    onChange={(v) => setMapping({ ...mapping, packagePriceGrossColumn: v })}
                    label="VPE-Preis Brutto"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <ColumnSelect
                    value={mapping.priceNetColumn}
                    onChange={(v) => setMapping({ ...mapping, priceNetColumn: v })}
                    label="Stückpreis Netto"
                  />
                  <ColumnSelect
                    value={mapping.priceGrossColumn}
                    onChange={(v) => setMapping({ ...mapping, priceGrossColumn: v })}
                    label="Stückpreis Brutto"
                  />
                </div>
                
                <ColumnSelect
                  value={mapping.packageSizeColumn}
                  onChange={(v) => setMapping({ ...mapping, packageSizeColumn: v })}
                  label="VPE-Größe"
                />
                
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={mapping.skipFirstRow}
                    onCheckedChange={(v) => setMapping({ ...mapping, skipFirstRow: v })}
                  />
                  <Label className="text-sm cursor-pointer">
                    Erste Zeile überspringen (Kopfzeile)
                  </Label>
                </div>
              </motion.div>
            )}

            {/* Import Button */}
            <Button
              onClick={handleImport}
              disabled={!file || !selectedSupplier || importing}
              className="w-full bg-rose-600 hover:bg-rose-700"
            >
              {importing ? "Importiere..." : "Preise importieren"}
            </Button>

            {/* Result */}
            {result && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`p-4 rounded-lg ${result.updated > 0 ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {result.updated > 0 ? (
                    <Check className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  )}
                  <span className="font-medium">
                    {result.updated} Preise aktualisiert
                  </span>
                </div>
                {result.not_found_count > 0 && (
                  <div className="text-sm text-slate-600">
                    <p>{result.not_found_count} Artikelnummern nicht gefunden:</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {result.not_found_articles?.slice(0, 10).join(", ")}
                      {result.not_found_count > 10 && ` ... und ${result.not_found_count - 10} weitere`}
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Vorschau
              {preview && (
                <span className="text-sm font-normal text-slate-500">
                  ({preview.total_rows} Zeilen)
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {preview ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {preview.headers?.map((header, idx) => (
                        <TableHead key={idx} className="text-xs whitespace-nowrap">
                          <div className="text-slate-400">Spalte {idx + 1}</div>
                          {header || "(leer)"}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.preview_rows?.map((row, rowIdx) => (
                      <TableRow key={rowIdx}>
                        {row.map((cell, cellIdx) => (
                          <TableCell key={cellIdx} className="text-sm">
                            {cell || "-"}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Laden Sie eine CSV-Datei hoch, um eine Vorschau zu sehen</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Instructions */}
      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="pt-6">
          <h3 className="font-medium mb-3">Anleitung</h3>
          <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
            <li>Zuerst Produkte und Lieferanten im System anlegen</li>
            <li>Produkte mit Artikelnummern bei Lieferanten verknüpfen (unter Produkte → Lieferantenpreise)</li>
            <li>CSV-Datei vom Lieferanten hier hochladen</li>
            <li>Spalten zuordnen (Artikelnummer ist Pflicht)</li>
            <li>System aktualisiert automatisch alle Preise anhand der Artikelnummern</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImportPage;
